import React from 'react';
import { C } from '../../../styles/theme';

/* ── Brand tokens ─────────────────────────────────────────── */
export { C };export const FONT = "'Plus Jakarta Sans', sans-serif";
export const PIE_COLORS = ['#2563eb', '#64748b', '#94a3b8', '#0891b2', '#d97706', '#7c3aed'];

/* ── Date helpers ─────────────────────────────────────────── */
const toLocal = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

export const getToday = () => toLocal(new Date());

export const getStartOfWeek = () => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return toLocal(d);
};

export const getEndOfWeek = () => {
    const d = new Date();
    const day = d.getDay();
    d.setDate(d.getDate() + (day === 0 ? 0 : 7 - day));
    return toLocal(d);
};

export const getStartOfMonth = () => {
    const d = new Date();
    return toLocal(new Date(d.getFullYear(), d.getMonth(), 1));
};

export const getStartOfYear = () => {
    const d = new Date();
    return toLocal(new Date(d.getFullYear(), 0, 1));
};

export const PRESETS = [
    { key: 'today', label: 'today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'year', label: 'This Year' },
    { key: 'custom', label: 'Custom' },
];

/* ── Date label formatters ───────────────────────────────── */
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDateLabel(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    return DAY_NAMES[d.getDay()] + ' ' + d.getDate();
}

export function formatWeekLabel(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    return 'W' + Math.ceil(d.getDate() / 7) + ' ' + MONTH_SHORT[d.getMonth()];
}

export function formatMonthLabel(dateStr) {
    const d = new Date(dateStr + 'T12:00:00');
    return MONTH_SHORT[d.getMonth()] + ' ' + d.getFullYear().toString().slice(-2);
}

export function toLocalISO(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function fillDateRange(data, startDate, endDate) {
    const map = {};
    data.forEach(r => { map[r.sales_date] = r; });
    const result = [];
    const d = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');
    while (d <= end) {
        const key = toLocalISO(d);
        result.push(map[key] || { sales_date: key, revenue: 0, transactions: 0 });
        d.setDate(d.getDate() + 1);
    }
    return result;
}

/* ── Format helpers ───────────────────────────────────────── */
export const fmt = (v, cur) => {
    if (v == null) return cur + '0';
    return cur + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const sectionLabel = {
    fontFamily: FONT, fontSize: 11, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 16,
};

/* ── Sparkline (mini SVG) ─────────────────────────────────── */
export const Sparkline = ({ data, width = 60, height = 20, color = C.green }) => {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const points = data.map((v, i) => {
        const x = (i / (data.length - 1)) * width;
        const y = height - ((v - min) / range) * height;
        return `${x},${y}`;
    }).join(' ');
    return (
        <svg width={width} height={height} style={{ display: 'block', marginTop: 8 }}>
            <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

/* ── Icon components ──────────────────────────────────────── */
export const ChartIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
);

export const DownloadIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
);

export const ExpandIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
        <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
    </svg>
);

export const InfoIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
);

export const WarningIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

export const TrendUpIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
    </svg>
);

export const TargetIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.mutedLight} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
    </svg>
);

/* ── Reusable components ──────────────────────────────────── */
export const EmptyState = ({ title }) => (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ marginBottom: 12, opacity: 0.5 }}><ChartIcon /></div>
        <div style={{ fontFamily: FONT, fontSize: 13, color: C.muted }}>{title}</div>
    </div>
);

export const BarListItem = ({ label, value, maxValue, color, cur, subtitle, pct }) => {
    const barPct = maxValue > 0 ? (value / maxValue) * 100 : 0;
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text }}>{label}</span>
                <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: C.text }}>
                    {fmt(value, cur)}{pct != null ? ` (${pct.toFixed(1)}%)` : ''}
                </span>
            </div>
            <div style={{ height: 8, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: barPct + '%', background: color || C.green, borderRadius: 4, transition: 'width 0.3s' }} />
            </div>
            {subtitle && <div style={{ fontFamily: FONT, fontSize: 11, color: C.muted, marginTop: 4 }}>{subtitle}</div>}
        </div>
    );
};

export const SectionCard = ({ children, style }) => (
    <div style={{ background: C.white, border: '1.5px solid ' + C.border, borderRadius: 12, padding: 20, ...style }}>
        {children}
    </div>
);

export const SectionDivider = ({ label, tag, tagColor = C.greenDark, tagBg = C.greenLight }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '32px 0 20px' }}>
        <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.mutedLight, whiteSpace: 'nowrap' }}>
            {label}
        </span>
        <span style={{
            fontFamily: FONT, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
            padding: '3px 8px', borderRadius: 4, background: tagBg, color: tagColor, whiteSpace: 'nowrap',
        }}>
            {tag}
        </span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
    </div>
);
